import { useEffect, useRef, useState, useCallback } from "react";
import { devLog } from "../utils/devLog";
import { fetchTransactionStatus } from "../api/api";

// Polling-fallback cadence. Tight early because most gateway outcomes land within a few
// seconds; relaxed afterwards so a long-running PIX payment does not keep hitting the shared
// database. Both stay well inside the server-side per-transaction rate limit.
const FAST_POLL_MS = 2000;
const FAST_POLL_WINDOW_MS = 30000;
const SLOW_POLL_MS = 5000;
const POLL_MAX_DURATION_MS = 10 * 60 * 1000;

const isTerminalStatus = (status) => status === "success" || status === "failed";

// Get tenant ID from the hostname

// add websocket in the checkout page 
const useWebSocket = (id, onMessage, onOpen, onClose, onError) => {
  const [connectionStatus, setConnectionStatus] = useState("Connecting...");
  const [lastMessage, setLastMessage] = useState(null);
  const [messages, setMessages] = useState([]);
  const [paymentStatus, setPaymentStatus] = useState("pending");
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;
  const reconnectTimeoutId = useRef(null);
  const terminalDelivered = useRef(false);
  const paymentStatusRef = useRef("pending");
  const onMessageRef = useRef(onMessage);

  const wsRef = useRef(null);

  const connect = useCallback(() => {
    try {
      if (!id) return;

      devLog("Connecting to WebSocket with ID:", id);

      // 🔗 Connect to your backend WebSocket

    const rawWsBaseUrl = process.env.REACT_APP_WS_BASE_URL;
    const tenantId = window.location.hostname.split(".")[0];

    if (!rawWsBaseUrl || !tenantId) {
      console.error("Missing WebSocket configuration.");
      return;
    }

    const wsBaseUrl = rawWsBaseUrl.replace("{tenant_id}", tenantId);
    const wsUrl = `${wsBaseUrl}/ws?checkoutToken=${id}`;
    devLog("Connecting to WebSocket at:", wsUrl);

    wsRef.current = new WebSocket(wsUrl);

      // onmessage
      wsRef.current.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          setLastMessage(message);
          devLog("Message from server:", event.data);
          setMessages((prev) => [...prev, event.data]);

          // Report a terminal outcome to the consumer at most once. Consumers fire
          // onParentCallback straight from here, so a second "success" — a duplicate webhook,
          // or the polling fallback racing this push — would double-notify the parent page.
          const incomingStatus = message.status?.toLowerCase();
          if (isTerminalStatus(incomingStatus)) {
            if (terminalDelivered.current) {
              devLog("Ignoring duplicate terminal status from socket:", incomingStatus);
              return;
            }
            terminalDelivered.current = true;
          }

          if (onMessage) {
            onMessage(event);
          }

          switch (message.status) {
            case "connection_established":
              setConnectionStatus("Connected");
              break;
            case "pending":
              setPaymentStatus(
                message.data?.status?.toLowerCase() || "processing"
              );
              break;
            case "success":
              setPaymentStatus("success");
              break;
            case "failed":
              setPaymentStatus("failed");
              break;
            case "pong":
              break;
            default:
              devLog("Unknown message type:", message.type);
          }

          if (message.type === "payment_update" && message.status) {
            setPaymentStatus(message.status.toLowerCase());
          }
        } catch (error) {
          console.error("Error parsing WebSocket message:", error);
        }
      };

      // onOpen
      wsRef.current.onopen = (event) => {
        if (onOpen) onOpen(event);
        devLog("WebSocket Connected");
        setConnectionStatus("Connected");
        reconnectAttempts.current = 0;
      };

      // onClose
      wsRef.current.onclose = (event) => {
        // if (onClose) onClose(event);
        devLog("WebSocket Disconnected:", event.code, event.reason);
        setConnectionStatus("Disconnected");

        // Attempt to reconnect if not a deliberate close
        if (
          event.code !== 1000 &&
          reconnectAttempts.current < maxReconnectAttempts
        ) {
          reconnectAttempts.current++;
          devLog(
            `Attempting to reconnect... (${reconnectAttempts.current}/${maxReconnectAttempts})`
          );

          reconnectTimeoutId.current = setTimeout(() => {
            connect();
          }, Math.pow(2, reconnectAttempts.current) * 1000); // Exponential backoff
        }
      };

      wsRef.current.onerror = (error) => {
        // if (onError) onError(error);
        console.error("WebSocket Error:", error);
        setConnectionStatus("Error");
      };
    } catch (error) {
      console.error("WebSocket connection error:", error);
      setConnectionStatus("Error");
    }
  }, [id]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutId.current) {
      clearTimeout(reconnectTimeoutId.current);
    }

    if (wsRef.current) {
      wsRef.current.close(1000, "Component unmounting");
    }
  }, []);

  const sendMessage = useCallback((message) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    }
  }, []);

  useEffect(() => {
    connect();

    return () => {
      disconnect();
    };
  }, [connect, disconnect]);

  // PIX flows: the user typically backgrounds the tab (or loses network) while paying in
  // their banking app, which kills the socket and exhausts the capped reconnect attempts.
  // When the tab becomes visible again or the network returns, reset the attempt counter
  // and reconnect if the socket is no longer open.
  useEffect(() => {
    if (!id) return undefined;

    const reconnectIfDead = () => {
      const state = wsRef.current?.readyState;
      if (state === WebSocket.OPEN || state === WebSocket.CONNECTING) return;

      devLog("WebSocket dead on resume — reconnecting");
      reconnectAttempts.current = 0;
      if (reconnectTimeoutId.current) {
        clearTimeout(reconnectTimeoutId.current);
      }
      connect();
    };

    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        reconnectIfDead();
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("online", reconnectIfDead);
    window.addEventListener("focus", reconnectIfDead);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("online", reconnectIfDead);
      window.removeEventListener("focus", reconnectIfDead);
    };
  }, [id, connect]);

  // A retry mints a new transaction id on the same mounted component. Without this reset the
  // previous attempt's delivery would suppress the new one's outcome.
  useEffect(() => {
    terminalDelivered.current = false;
    paymentStatusRef.current = "pending";
  }, [id]);

  useEffect(() => {
    paymentStatusRef.current = paymentStatus;
  }, [paymentStatus]);

  // Consumers pass an inline arrow, so onMessage is a new function on every render. Keep the
  // latest one in a ref rather than in the poll closure, which is keyed on the id alone.
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  // Polling fallback for the payment outcome.
  //
  // WebSocketHandler on the backend keeps its sessions in a per-JVM map, so a push only
  // reaches the instance holding this socket. With more than one instance the provider
  // webhook is consumed by whichever instance polls SQS first, and any push from a different
  // one is dropped silently — the payment succeeds and the page spins forever. Polling makes
  // that miss recoverable. It stays useful even once a pub/sub backplane lands, because pub/sub
  // is fire-and-forget and a push can still be lost to a reconnect or a backgrounded tab.
  useEffect(() => {
    if (!id) return undefined;

    let cancelled = false;
    let timeoutId = null;
    const startedAt = Date.now();

    const nextDelay = () =>
      Date.now() - startedAt < FAST_POLL_WINDOW_MS ? FAST_POLL_MS : SLOW_POLL_MS;

    const schedule = () => {
      if (cancelled) return;
      timeoutId = setTimeout(poll, nextDelay());
    };

    const poll = async () => {
      if (cancelled) return;

      if (
        terminalDelivered.current ||
        isTerminalStatus(paymentStatusRef.current) ||
        Date.now() - startedAt > POLL_MAX_DURATION_MS
      ) {
        return;
      }

      // A hidden tab is not waiting on the result; the visibilitychange listener below
      // polls immediately on resume.
      if (document.visibilityState !== "visible") {
        schedule();
        return;
      }

      try {
        const data = await fetchTransactionStatus(id);
        const status = data?.status?.toLowerCase();

        if (!cancelled && isTerminalStatus(status) && !terminalDelivered.current) {
          devLog("Payment outcome resolved by polling fallback:", status);
          terminalDelivered.current = true;
          setPaymentStatus(status);
          // Consumers read event.data, so hand them the same shape the socket delivers.
          onMessageRef.current?.({ data: JSON.stringify({ status }) });
          return;
        }
      } catch (error) {
        // This is the fallback path — a failed poll must never surface to the shopper. A 404
        // before the transaction is committed, a 429, or an offline tab all just mean
        // "try again shortly"; the socket may still deliver first.
        devLog("Status poll failed:", error?.response?.status || error?.message);
      }

      schedule();
    };

    schedule();

    const pollOnVisible = () => {
      if (document.visibilityState !== "visible") return;
      if (timeoutId) clearTimeout(timeoutId);
      poll();
    };

    document.addEventListener("visibilitychange", pollOnVisible);
    window.addEventListener("online", pollOnVisible);

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
      document.removeEventListener("visibilitychange", pollOnVisible);
      window.removeEventListener("online", pollOnVisible);
    };
  }, [id]);

  return {
    connectionStatus,
    lastMessage,
    paymentStatus,
    sendMessage,
    messages,
    reconnect: connect,
    wsRef, // Include wsRef if you need direct access to WebSocket
  };
};

export default useWebSocket;
